import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useSite } from "../context/SiteContext";

const Layout = ({ children }) => {
  const { meta, refresh } = useSite();
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const location = useLocation();
  const nav = (meta?.nav || []).filter((n) => !n.is_home);

  const linkTo = (item) => (item.is_home ? "/" : `/p/${item.slug}`);

  useEffect(() => {
    const closeMobileOnDeck = () => {
      if (document.documentElement.dataset.slideDeck) setOpen(false);
    };
    closeMobileOnDeck();
    const observer = new MutationObserver(closeMobileOnDeck);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-slide-deck"],
    });
    return () => observer.disconnect();
  }, []);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    const before = meta?.last_sync;
    const t = toast.loading("Synchronisation depuis Notion…");
    try {
      await api.post("/sync");
      let done = false;
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data } = await api.get("/sync/status");
        if (data?.last_sync && data.last_sync !== before) {
          done = data.status === "ok";
          break;
        }
      }
      if (done) {
        toast.success("Contenu à jour", { id: t });
        refresh();
        setTimeout(() => window.location.reload(), 600);
      } else {
        toast.error("Synchronisation échouée ou trop longue", { id: t });
        setSyncing(false);
      }
    } catch (e) {
      toast.error("Impossible de lancer la synchronisation", { id: t });
      setSyncing(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-pillet-bg pt-16">
      <header
        className="fixed top-0 inset-x-0 z-[60] bg-pillet-forest border-b border-pillet-sage/30"
        data-testid="site-header"
      >
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-home">
            <img
              src="/pillet-logo.svg"
              alt="Pillet SA"
              className="h-10 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {nav.map((item) => (
              <NavLink
                key={item.slug}
                to={linkTo(item)}
                end={item.is_home}
                data-testid={`nav-${item.slug}`}
                className={({ isActive }) =>
                  `px-3 py-2 font-heading uppercase text-xs tracking-widest transition-colors ${
                    isActive ? "text-pillet-sage" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {item.is_home ? "Accueil" : item.title}
              </NavLink>
            ))}
            <button
              onClick={handleSync}
              disabled={syncing}
              data-testid="sync-btn"
              className="ml-2 p-2 text-white/70 hover:text-pillet-sage transition-colors disabled:opacity-60"
              title="Rafraîchir depuis Notion"
            >
              <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
            </button>
          </nav>

          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <nav className="md:hidden bg-pillet-forest border-t border-pillet-sage/20 px-5 py-3 flex flex-col gap-1">
            {nav.map((item) => (
              <NavLink
                key={item.slug}
                to={linkTo(item)}
                end={item.is_home}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-2 py-2.5 font-heading uppercase text-sm tracking-wider ${
                    isActive ? "text-pillet-sage" : "text-white/80"
                  }`
                }
              >
                {item.is_home ? "Accueil" : item.title}
              </NavLink>
            ))}
            <button
              onClick={() => { setOpen(false); handleSync(); }}
              disabled={syncing}
              data-testid="sync-btn-mobile"
              className="text-left px-2 py-2.5 font-heading uppercase text-sm tracking-wider text-white/70 flex items-center gap-2 disabled:opacity-60"
            >
              <RefreshCw size={15} className={syncing ? "animate-spin" : ""} /> Rafraîchir depuis Notion
            </button>
          </nav>
        )}
      </header>

      <main className="flex-1 w-full" key={location.pathname}>
        {children}
      </main>

      <footer className="bg-pillet-forest border-t border-pillet-sage/30 mt-16">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-heading font-bold text-white uppercase tracking-wide">Pillet SA</p>
            <p className="text-pillet-muted text-xs mt-1">
              Portail interactif — livrables de l'audit · Memoways
            </p>
          </div>
          <p className="text-pillet-muted text-xs">
            Contenu synchronisé depuis Notion · lecture seule
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
